import type { Issue } from '../types/issue';
import { computePlan, computeActual, type PlanInfo } from './plan';
import { num } from './format';
import { isDone } from './status';

// Erweitertes Epic mit aggregierten Plan/Ist-Daten und Kids
export type Epic = Issue & {
  _kids: Child[];
  _epicRough: number;
  _epicRest: number;
  _refinedKidPlan: number;   // Σ SP/Estimate aller refined Kids
  _totalPlan: number;
  _totalActual: number;
  _kidCount: number;
  _doneCount: number;
  _refinedCount: number;
  _spCount: number;          // Anzahl Kids mit gepflegtem SP > 0
  _isOrphanContainer?: boolean;
};

// Drift-Kennzahlen für die Epic-Drift-Tabelle. Werte sind None-tolerant —
// bei _epicRough = 0 (kein Rough gepflegt) liefern wir null statt Infinity.
export type EpicDrift = {
  rough:        number;
  refinedPlan:  number;
  drift:        number;       // refinedPlan − rough (positiv = über Rough)
  driftPct:     number | null;
  refinementPct:number | null; // % der Kids, die refined sind
  spCoveragePct:number | null; // % der Kids, die SP gepflegt haben
  confidencePct:number | null; // Mittelwert aus refinementPct und spCoveragePct
  // Kids, die NICHT zum Release des Epics gehören (eigene Fix Version anderswo)
  kidsOutOfRelease: number;
};

// Default-Drift über alle refined Kids (alte Logik) — bleibt für
// Rückwärtskompatibilität. Aufruf-Stellen sollten driftInRelease() nutzen.
export function driftOf(e: Epic): EpicDrift {
  const rough       = e._epicRough || 0;
  const refinedPlan = e._refinedKidPlan || 0;
  const drift       = refinedPlan - rough;
  const driftPct    = rough > 0 ? (drift / rough) * 100 : null;

  const kid = e._kidCount || 0;
  const refinementPct = kid > 0 ? (e._refinedCount / kid) * 100 : null;
  const spCoveragePct = kid > 0 ? (e._spCount       / kid) * 100 : null;
  const confidencePct =
    refinementPct == null && spCoveragePct == null
      ? null
      : ((refinementPct ?? 0) + (spCoveragePct ?? 0)) / 2;

  return { rough, refinedPlan, drift, driftPct, refinementPct, spCoveragePct, confidencePct, kidsOutOfRelease: 0 };
}

// Drift, die nur Kids mit gleicher Release-Zuordnung wie das Epic zählt.
// Wesentlich realistischer für die "Wie weit liegt Detailplanung von
// Grobplanung ab"-Frage, weil Kids mit eigener Fix Version (anderes
// Release oder gar keine) das Bild sonst aufblähen.
export function driftInRelease(
  e: Epic,
  byKey: Record<string, Issue>,
  knownReleases: string[],
  detectRelease: (epic: Issue, known: string[]) => string,
  releaseOfIssue: (item: Issue, byKey: Record<string, Issue>, known: string[]) => string,
): EpicDrift {
  const epicRelease = detectRelease(e, knownReleases);
  const kidsInRelease = (e._kids ?? []).filter(k =>
    releaseOfIssue(k, byKey, knownReleases) === epicRelease
  );

  const rough = e._epicRough || 0;

  // Refined Plan nur über Kids, die wirklich zum Epic-Release gehören
  const refinedInRel = kidsInRelease.filter(k => k._plan.src === 'refined');
  const refinedPlan  = refinedInRel.reduce((s, k) => s + (k._plan.val || 0), 0);

  const drift    = refinedPlan - rough;
  const driftPct = rough > 0 ? (drift / rough) * 100 : null;

  // Confidence-Bausteine ebenfalls nur über Same-Release-Kids
  const totalIn = kidsInRelease.length;
  const refinedCount = refinedInRel.length;
  const spCount = kidsInRelease.filter(k => {
    const sp = num(k['Custom field (Story Points)']);
    return sp != null && sp > 0;
  }).length;

  const refinementPct = totalIn > 0 ? (refinedCount / totalIn) * 100 : null;
  const spCoveragePct = totalIn > 0 ? (spCount       / totalIn) * 100 : null;
  const confidencePct =
    refinementPct == null && spCoveragePct == null
      ? null
      : ((refinementPct ?? 0) + (spCoveragePct ?? 0)) / 2;

  const kidsOutOfRelease = (e._kidCount || 0) - totalIn;

  return { rough, refinedPlan, drift, driftPct, refinementPct, spCoveragePct, confidencePct, kidsOutOfRelease };
}

// Kid (Story/Bug) hat per Item-Plan/Actual annotations
export type Child = Issue & {
  _plan: PlanInfo;
  _actual: PlanInfo;
};

export type DataModel = {
  epics: Epic[];
  byKey: Record<string, Issue>;
  orphans: Child[];
};

const ORPHAN_KEY = '__orphans__';

// Plan/Ist je Item + Epic-Tree aufbauen. Identisch zur V1-Logik:
//   Story/Bug-Plan zählt nur "eigenständig", wenn refined (Status ≥ Ready)
//   und SP/Est gepflegt sind. Sonst deckt der Epic-Rough.
//
//   totalPlan   = max(epicRough, Σ refinedKidPlan)
//   totalActual = Σ ActualDoneKids + Σ Plan(refinedOpenKids) + epicRest
export function buildModel(rows: Issue[], spToPtFactor = 1.0): DataModel {
  const byKey: Record<string, Issue> = {};
  for (const r of rows) {
    if (typeof r['Issue key'] === 'string' && r['Issue key']) byKey[r['Issue key']] = r;
  }

  const epics: Epic[] = [];
  const childrenByEpic: Record<string, Child[]> = {};
  const orphans: Child[] = [];

  // Pass 1: Epics anlegen, Kids vorbereiten (mit per-Item Plan/Actual)
  for (const r of rows) {
    if (r['Issue Type'] === 'Epic') {
      const e = r as Epic;
      e._kids = [] as Child[];
      epics.push(e);
      childrenByEpic[r['Issue key']] = [];
    }
  }

  // Pass 2: Stories/Bugs → Plan/Ist und Parent-Zuordnung
  for (const r of rows) {
    if (r['Issue Type'] !== 'Story' && r['Issue Type'] !== 'Bug') continue;
    const child = r as Child;
    child._plan   = computePlan(r, spToPtFactor);
    child._actual = computeActual(r, child._plan);
    const pk = r['Parent key'];
    if (pk && childrenByEpic[pk] !== undefined) {
      childrenByEpic[pk].push(child);
    } else {
      orphans.push(child);
    }
  }

  // Pass 3: Aggregation pro Epic
  for (const e of epics) {
    const kids = childrenByEpic[e['Issue key']] ?? [];
    const refinedKids = kids.filter(k => k._plan.src === 'refined');
    const refinedKidPlan = refinedKids.reduce((s, k) => s + (k._plan.val || 0), 0);
    const epicRough = num(e['Custom field (Rough Estimation (d))']) || 0;
    const epicRest  = Math.max(0, epicRough - refinedKidPlan);
    const totalPlan = refinedKidPlan + epicRest;

    const doneKids = kids.filter(k => isDone(k['Status'] ?? ''));
    const actualDone = doneKids.reduce((s, k) => s + (k._actual.val || 0), 0);
    const actualOpenRefined = refinedKids
      .filter(k => !isDone(k['Status'] ?? ''))
      .reduce((s, k) => s + (k._plan.val || 0), 0);
    const totalActual = actualDone + actualOpenRefined + epicRest;

    // Confidence-Bausteine: wie viele Kids haben einen *gepflegten* SP > 0
    const spCount = kids.filter(k => {
      const sp = num(k['Custom field (Story Points)']);
      return sp != null && sp > 0;
    }).length;

    e._kids = kids;
    e._epicRough = epicRough;
    e._epicRest = epicRest;
    e._refinedKidPlan = refinedKidPlan;
    e._totalPlan = totalPlan;
    e._totalActual = totalActual;
    e._kidCount = kids.length;
    e._doneCount = doneKids.length;
    e._refinedCount = refinedKids.length;
    e._spCount = spCount;
  }

  // Pseudo-Epic für Items ohne gültigen Parent
  if (orphans.length > 0) {
    const refinedOrphans = orphans.filter(k => k._plan.src === 'refined');
    const doneOrphans = orphans.filter(k => isDone(k['Status'] ?? ''));
    const refinedKidPlan = refinedOrphans.reduce((s, k) => s + (k._plan.val || 0), 0);
    const actualDone = doneOrphans.reduce((s, k) => s + (k._actual.val || 0), 0);
    const actualOpenRefined = refinedOrphans
      .filter(k => !isDone(k['Status'] ?? ''))
      .reduce((s, k) => s + (k._plan.val || 0), 0);

    const pseudo: Epic = {
      'Issue key': ORPHAN_KEY,
      'Issue Type': 'Epic',
      'Summary': `Items ohne Epic-Parent (${orphans.length})`,
      'Status': '',
      'Team Name': '',
      'Priority': '',
      'Assignee': '',
      'Parent key': '',
      _kids: orphans,
      _epicRough: 0,
      _epicRest: 0,
      _refinedKidPlan: refinedKidPlan,
      _totalPlan: refinedKidPlan,
      _totalActual: actualDone + actualOpenRefined,
      _kidCount: orphans.length,
      _doneCount: doneOrphans.length,
      _refinedCount: refinedOrphans.length,
      _spCount: orphans.filter(k => {
        const sp = num(k['Custom field (Story Points)']);
        return sp != null && sp > 0;
      }).length,
      _isOrphanContainer: true,
    };
    epics.push(pseudo);
  }

  return { epics, byKey, orphans };
}
